import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getPlatformStats } from "../api/games";
import { getHeroGames } from "../data/heroGames";
import { useAuth } from "../context/AuthContext";
import "./Hero.css";

/**
 * Hero-секция лендинга — как у roblox.com: слева текст+CTA, справа диагональный
 * коллаж карточек игр, который бесконечно плывёт. Фон — анимированные градиентные
 * пятна + сетка.
 *
 * Превью игр для коллажа берутся ЛОКАЛЬНО (public/games-preview/ + manifest.json),
 * не с бэкенда — раньше фид Storys нёс ~300 КБ base64 и коллаж долго грузился.
 * С бэкенда тянется только лёгкая статистика платформы (счётчики).
 *
 * Коллаж: 3 вертикальные колонки наклонены, каждая бесконечно скроллится
 * (вверх/вниз поочерёдно) CSS-анимацией. Карточки дублируются ×2 для бесшовности.
 */
export default function Hero() {
  const { isAuthenticated } = useAuth();
  const [games, setGames] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const ctrl = new AbortController();

    // превью игр — из локального манифеста (быстро, кешируется браузером)
    getHeroGames(ctrl.signal)
      .then(setGames)
      .catch(() => {});

    // статистика платформы — лёгкий запрос к API (если упадёт — просто не покажем)
    getPlatformStats(ctrl.signal).then(setStats).catch(() => {});

    return () => ctrl.abort();
  }, []);

  // раскидываем игры по 3 колонкам
  const columns = [[], [], []];
  games.forEach((g, i) => columns[i % 3].push(g));
  const hasGames = games.length >= 3;

  return (
    <section className="rb-hero">
      {/* анимированный фон: градиентные пятна + сетка */}
      <div className="rb-hero__bg" aria-hidden>
        <div className="rb-hero__blob rb-hero__blob--1" />
        <div className="rb-hero__blob rb-hero__blob--2" />
        <div className="rb-hero__blob rb-hero__blob--3" />
        <div className="rb-hero__grid-overlay" />
      </div>

      <div className="rb-container rb-hero__inner">
        <div className="rb-hero__text">
          <div className="rb-hero__badge">
            <span className="rb-hero__badge-dot" />
            Игровая платформа · Альфа-версия
          </div>

          <h1 className="rb-hero__title">
            Играй и создавай&nbsp;игры
            <span className="rb-hero__title-accent"> бесплатно</span>
          </h1>
          <p className="rb-hero__lead">
            Рублокс — 3D-платформа экосистемы онлайн-школы «Майнкрафтия».
            Тысячи игр от сообщества, свой редактор и скрипты на JavaScript.
          </p>

          {/* Кнопки зависят от состояния входа: гостю предлагаем начать,
              вошедшему — вернуться к играм. Показывать «Скачать
              приложение» человеку, который уже играет, бессмысленно. */}
          <div className="rb-hero__cta">
            {isAuthenticated ? (
              <>
                <Link to="/app" className="rb-btn rb-btn--primary rb-btn--lg">
                  <span>В кабинет</span>
                </Link>
                <Link to="/app/games" className="rb-btn rb-btn--ghost rb-btn--lg">
                  Каталог игр
                </Link>
              </>
            ) : (
              <>
                <Link to="/signup" className="rb-btn rb-btn--primary rb-btn--lg">
                  <span>Начать бесплатно</span>
                </Link>
                <a href="#games" className="rb-btn rb-btn--ghost rb-btn--lg">
                  Смотреть игры
                </a>
              </>
            )}
          </div>

          {stats && (stats.games > 0 || stats.plays > 0) && (
            <div className="rb-hero__stats">
              <div className="rb-hero__stat">
                <strong>{formatCount(stats.games)}</strong>
                <span>игр на платформе</span>
              </div>
              <div className="rb-hero__stat">
                <strong>{formatCount(stats.plays)}</strong>
                <span>запусков</span>
              </div>
              <div className="rb-hero__stat">
                <strong>{formatCount(stats.likes)}</strong>
                <span>лайков</span>
              </div>
            </div>
          )}
        </div>

        {/* Коллаж карточек.
            Десктоп — 3 вертикальные колонки, плывут вверх/вниз.
            Мобиле — те же колонки в ряд = горизонтальная плывущая лента.
            Колонки дублируются ×2 (cols 4-6 скрыты на десктопе через CSS) —
            это даёт бесшовный горизонтальный цикл translateX(-50%) на мобиле. */}
        <div className="rb-hero__showcase" aria-hidden={!hasGames}>
          <div className="rb-hero__showcase-fade rb-hero__showcase-fade--top" />
          <div className="rb-hero__showcase-fade rb-hero__showcase-fade--bottom" />
          <div className="rb-hero__columns">
            {[...columns, ...columns].map((col, ci) => {
              // ci 0-2 — оригинальные колонки, 3-5 — дубль (скрыт на десктопе)
              const baseIdx = ci % 3;
              return (
                <div
                  key={ci}
                  className={`rb-hero__column rb-hero__column--${baseIdx + 1} ${
                    ci >= 3 ? "rb-hero__column--dup" : ""
                  }`}
                >
                  {/* внутри колонки набор ещё ×2 — для вертикального цикла */}
                  {(hasGames ? [...col, ...col] : placeholderTiles()).map(
                    (g, i) => (
                      <HeroTile key={`${ci}-${i}`} game={g} />
                    )
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/** Карточка в коллаже — превью + название (или плейсхолдер-шиммер). */
function HeroTile({ game }) {
  if (!game) {
    return <div className="rb-hero__tile rb-hero__tile--ph" />;
  }
  return (
    <div className="rb-hero__tile" title={game.title}>
      <img src={game.thumbnail} alt={game.title} loading="lazy" />
      <span className="rb-hero__tile-title">{game.title}</span>
    </div>
  );
}

/** Пока игры не загрузились — 6 пустых плиток на колонку. */
function placeholderTiles() {
  return Array.from({ length: 6 }).map(() => null);
}

/** 12345 → "12 345", 1200000 → "1,2 млн" */
function formatCount(n) {
  if (n >= 1_000_000)
    return (n / 1_000_000).toFixed(1).replace(".", ",") + " млн";
  return n.toLocaleString("ru-RU");
}
